import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import AddNotesFrais from "./AddNotesFrais";
import { Button } from "../../../_helpers/components/Button";
import {
    getMyExpenseNotes,
    getExpenseNote,
    createExpenseNote,
    updateExpenseNote,
} from "../../../_api/ExpenseNote";
import { getCurrencySymbol } from "./currencies";

const STATUS_LABELS = {
    PENDING: "En attente",
    ACCEPTED: "Validée",
    REFUSED: "Refusée",
};

const getStatutStyle = (status) => {
    switch (status) {
        case "ACCEPTED":
            return "text-green-600 bg-green-100";
        case "PENDING":
            return "text-yellow-600 bg-yellow-100";
        case "REFUSED":
            return "text-red-600 bg-red-100";
        default:
            return "text-gray-600 bg-gray-100";
    }
};

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
    return d.toISOString().slice(0, 10);
};

const ListeNotesFrais = () => {
    const [openSidebar, setOpenSidebar] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [noteDetail, setNoteDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchNotes = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getMyExpenseNotes();
            setNotes(res.data || res);
        } catch (err) {
            setError(err?.response?.data?.message || "Erreur lors du chargement.");
            setNotes([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotes();
    }, []);

    const handleSubmit = async (formData) => {
        try {
            const form = new FormData();
            form.append("date", formData.date);
            form.append("type", formData.type);
            form.append("amount", formData.montant);
            form.append("currency", formData.currency || "EUR");
            form.append("comment", formData.commentaire || "");
            if (formData.fichiers && formData.fichiers.length > 0) {
                formData.fichiers.forEach((file) => {
                    form.append("files[]", file);
                });
            }

            if (selectedNote) {
                await updateExpenseNote(selectedNote.id, form);
                toast.success("Note de frais mise à jour.");
            } else {
                await createExpenseNote(form);
                toast.success("Note de frais créée.");
            }
            setOpenSidebar(false);
            setSelectedNote(null);
            fetchNotes();
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.errors
                    ? Object.values(err.response.data.errors).flat().join(", ")
                    : "Erreur lors de l'enregistrement.";
            toast.error(msg);
            throw err;
        }
    };

    const handleRowClick = async (note) => {
        setSelectedNote(note);
        setOpenSidebar(true);
        if (note?.id) {
            setLoadingDetail(true);
            setNoteDetail(null);
            try {
                const res = await getExpenseNote(note.id);
                setNoteDetail(res.data ?? res);
            } catch {
                setNoteDetail(note);
            } finally {
                setLoadingDetail(false);
            }
        } else {
            setNoteDetail(null);
        }
    };

    const displayNotes = notes.map((n) => ({
        ...n,
        date: formatDate(n.date),
        type: n.expense_type,
        montant: parseFloat(n.amount) || 0,
        currency: n.currency || "EUR",
        statutLabel: STATUS_LABELS[n.status] || n.status,
        commentaire: n.comment,
    }));

    return (
        <div className="p-4">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Mes notes de frais</h2>
                <Button
                    name="+ Ajouter une note de frais"
                    action={() => {
                        setSelectedNote(null);
                        setNoteDetail(null);
                        setOpenSidebar(true);
                    }}
                />
            </div>

            {loading && (
                <div className="text-gray-500 text-sm py-4">Chargement...</div>
            )}
            {error && (
                <div className="text-red-600 text-sm py-4">{error}</div>
            )}
            {!loading && !error && (
                <>
                    <div className="hidden md:block">
                        <table className="w-full table-auto border border-gray-200">
                            <thead>
                                <tr className="bg-gray-100 text-left text-sm">
                                    <th className="p-2">Date</th>
                                    <th className="p-2">Type</th>
                                    <th className="p-2">Montant</th>
                                    <th className="p-2">Statut</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayNotes.map((note) => (
                                    <tr
                                        key={note.id}
                                        className="border-t text-sm cursor-pointer hover:bg-gray-50"
                                        onClick={() => handleRowClick(note)}
                                    >
                                        <td className="p-2">{note.date}</td>
                                        <td className="p-2">{note.type}</td>
                                        <td className="p-2">
                                            {note.montant.toFixed(2)} {getCurrencySymbol(note.currency)}
                                        </td>
                                        <td className="p-2">
                                            <span
                                                className={`px-2 py-1 rounded-full text-xs ${getStatutStyle(
                                                    note.status
                                                )}`}
                                            >
                                                {note.statutLabel}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="md:hidden space-y-4">
                        {displayNotes.map((note) => (
                            <div
                                key={note.id}
                                className="border p-3 rounded-lg shadow-xs bg-white"
                                onClick={() => handleRowClick(note)}
                            >
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">
                                        {note.date}
                                    </span>
                                    <span
                                        className={`text-xs font-medium px-2 py-1 rounded-full ${getStatutStyle(
                                            note.status
                                        )}`}
                                    >
                                        {note.statutLabel}
                                    </span>
                                </div>
                                <div className="mt-2 text-sm">
                                    <div>
                                        <strong>Type:</strong> {note.type}
                                    </div>
                                    <div>
                                        <strong>Montant:</strong>{" "}
                                        {note.montant.toFixed(2)} {getCurrencySymbol(note.currency)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {displayNotes.length === 0 && !loading && !error && (
                <div className="text-gray-500 text-sm py-8 text-center">
                    Aucune note de frais.
                </div>
            )}

            <AddNotesFrais
                open={openSidebar}
                onClose={() => {
                    setOpenSidebar(false);
                    setSelectedNote(null);
                    setNoteDetail(null);
                }}
                onSubmit={handleSubmit}
                note={noteDetail ?? selectedNote}
                noteLoading={!!selectedNote && loadingDetail}
                editable={
                    !selectedNote || (noteDetail ?? selectedNote)?.status === "PENDING"
                }
            />
        </div>
    );
};

export default ListeNotesFrais;
